/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import { EventHandler, SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { Action } from "@ciaopeople/cp-shatsu/EventHandler/event-handler";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { isBoolean, isNumber, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { ExecutionId, VideoRef } from "../../player/MediaResource.js";
import { VideoContext } from "../../player/Player.js";
import { AdBreak, AdBreakType, AdCallResolver, AdUnit, ResolvedAdUnit, isAdBreak } from "../AdUnit.js";
import { AdContainerControls, AdsController } from "../AdsController.js";
import { AdsEnrichedVideoContext } from "../AdsEnrichedVideoContext.js";
import { GDPRSdk } from "../GDPR.js";
import { AdErrorStruct, PlayerAdBreakRelatedEvent, PlayerAdEvent, PlayerAdEventAction, PlayerAdEventListener, PlayerAdEventType, PlayerAdEventWithInfo, PlayerAdUnitRelatedEvent, isPlayerAdEvent } from "../PlayerAdEvent.js";
import { UserAdsData } from "../adcall/composable/UserAdsData.js";
import { PrebidModule } from "../prebid/PrebidModule.js";
import { isPrebidModuleWithAdsManagerOps } from "../prebid/PrebidModuleWithAdsManagerOps.js";
import { VideoPolicyRules } from "../vprules/VideoPolicyRules.js";
import { getGoogleType } from "./ImaLoader.js";
import { IMAUnmuter } from "../../unmuter/IMAUserGestureUnmuter.js";
import "./types/ima.js";



export const AD_EVENT_NAME = "ad_event";

export enum AdResumableTriggerBehaviour {

    //each group triggers for itself
    REGULAR = "regular",
    //as it says, defers preroll trigger to first midroll if it exists
    DEFER_PREROLL_TRIGGER_TO_FIRST_MIDROLL = "defer_preroll_to_midroll"
}


const DEFAULT_AD_VIEWABLE_IMPRESSION_TIME = 4100;

export type ImaOmidConfig = { [k in google.ima.OmidVerificationVendor]?: google.ima.OmidAccessMode }


export type ImaControllerFeatures = {
    vpaid?: google.ima.ImaSdkSettings.VpaidMode,
    mediaPreloading: boolean,
    styledAds: boolean,
    autoPlayAdBreaks: boolean,
    enableCustomClickTracking: boolean
}
/**
 * Google Ima3 based Ad Controller Config
 */
export type ImaControllerConfig = {
    features: ImaControllerFeatures,
    omid?: ImaOmidConfig,
    responsive: boolean
    adResumableTriggerBehaviour: AdResumableTriggerBehaviour,
    adImpressionViewableTimeMillis?: number,
    videoPolicy: VideoPolicyRules,
    ensureResourceLoadAfterPrerollPercentage?: number
}


type ContainerResizeEventData = {
    width: number,
    height: number,
    fullscreen: boolean
};


function isContainerResizeEventData(a: any): a is ContainerResizeEventData {
    return isObject(a) &&
        ("width" in a && isNumber(a.width)) &&
        ("height" in a && isNumber(a.height)) &&
        ("fullscreen" in a && isBoolean(a.fullscreen));
}


enum IMA_CONTROLLER_INTERNAL_EVENTS {


    CONTAINER_RESIZE = "container_resize"

}


function buildErrorStruct(w: Window, fatal: boolean, unitLabel: string, error: google.ima.AdError, adInfo: google.ima.Ad, video: VideoInfoRawData): AdErrorStruct {
    let connection: any;
    try {
        connection = w.navigator["connection"] || w.navigator["mozConnection"] || w.navigator["webkitConnection"];
    } catch (e) {
    }
    return {
        fatal: fatal ? "FATAL" : "",
        adunit: unitLabel,
        vastErrorCode: error ? error.getVastErrorCode() : 0,
        type: error ? error.getType() : "UNKNOWN_TYPE",
        errorCode: error ? error.getErrorCode() : 0,
        message: error ? error.getMessage() : "Unspecified error message",
        adId: adInfo ? adInfo.getAdId() : null,
        adSystem: adInfo ? adInfo.getAdSystem() : null,
        contentType: adInfo ? adInfo.getContentType() : null,
        mediaUrl: adInfo ? adInfo.getMediaUrl() : null,
        duration: adInfo ? adInfo.getDuration() : 0,
        linear: adInfo ? adInfo.isLinear() : false,
        title: adInfo ? adInfo.getTitle() : null,
        description: adInfo ? adInfo.getDescription() : null,
        userAgent: w.navigator.userAgent ? w.navigator.userAgent : "Unspecified",
        connection: connection && connection.type ? connection.type : "unknown",
        href: w.location.href,
        wrapperIds: adInfo ? adInfo.getWrapperAdIds() : null,
        wrapperSys: adInfo ? adInfo.getWrapperAdSystems() : null,
        vinfo: video
    }

}


export interface WrappedImaAdRequest {
    unit: AdUnit;
    resource: VideoRef;
    prepareRequest(): Promise<ResolvedAdUnit>;
    doRequest(): Promise<WrappedImaAdsManager>;
}

class WrappedImaAdRequestDefaultImpl {

    private preparePromise: Promise<ResolvedAdUnit>;

    private requestPromise: Promise<WrappedImaAdsManager>;

    constructor(public readonly unit: AdUnit,
        public readonly resource: VideoRef,
        private prep: () => Promise<ResolvedAdUnit>,
        private exec: (r: ResolvedAdUnit) => Promise<WrappedImaAdsManager>
    ) {


    }

    public prepareRequest(): Promise<ResolvedAdUnit> {
        if (!this.preparePromise) {
            this.preparePromise = this.prep();
        }
        return this.preparePromise;
    }

    public doRequest(): Promise<WrappedImaAdsManager> {
        if (!this.requestPromise) {
            this.requestPromise = this.prepareRequest().then(req => this.exec(req));
        }
        return this.requestPromise;
    }




}


/**
 * Wraps `google.ima.AdsManager`.
 * Instances of these objects could be shared to other 
 * system parts, like `ImaAdItem` implementations,
 * and let them ask for manager initialization operations
 * alongside with `AbstractImaController` implementation specific
 * operations.
 * 
 */
export interface WrappedImaAdsManager {

    readonly unit: AdUnit,
    resizeListener: Action;
    evtRetrigger(evt: google.ima.AdEvent): void;
    errRetrigger(evt: google.ima.AdErrorEvent): void;
    close(): void;
    /**
     * please use this method instead of using directly
     * [[AdsManager.init()]] method
     */
    init(): void;

    /**
     * please use this method instead of using directly
     * [[AdsManager.start()]] method
     */
    start(): Promise<void>;
    readonly adsManager: google.ima.AdsManager,
    adsLoader: google.ima.AdsLoader,

}




/**
 * these interface represents Ad Container GUI controls
*/
export interface ImaSpecificAdsContainerControls {


    /**
     * show ad container
     * @param mng 
     * @param adInfo 
     */
    show(mng?: google.ima.AdsManager, adInfo?: google.ima.Ad): void;
    /**
     * hide ad container
     */
    hide(): void;
    /**
     * refresh ad container
     * @param mng 
     * @param adInfo 
     */
    refresh(mng?: google.ima.AdsManager, adInfo?: google.ima.Ad): void;

    signalPlayStatus(newStatus: boolean): void;

    signalAudioStatus(newStatus: boolean): void;




}



const closeAdFun = (mng: google.ima.AdsManager) => mng.stop();

/**
 * Abstract base class for managing Google IMA SDK ad operations within a media player.
 * 
 * This controller handles initialization of the IMA SDK, ad container controls, ad requests,
 * event handling, and lifecycle management for ad playback. It provides a framework for
 * integrating GDPR, user ad data, prebid modules, and custom ad container controls.
 * 
 * Subclasses must implement `buildEnrichedVideoContext` to provide their own enriched video context logic.
 * 
 * @remarks
 * - Handles responsive resizing, custom click tracking, and GDPR compliance.
 * - Manages ad events and errors, and exposes hooks for player ad event listeners.
 * - Provides methods for creating ad requests, rendering settings, and destroying ad managers.
 * 
 * @template AdsController
 * 
 * @param ima - The Google IMA SDK namespace.
 * @param window - The browser window object.
 * @param gdpr - GDPR SDK for privacy compliance.
 * @param userAdData - User ad preferences and data.
 * @param prebid - Prebid module for header bidding integration.
 * @param imaControllerConfig - Configuration for IMA controller features and policies.
 * @param adResolver - Function to resolve ad calls.
 * @param adContainerControls - Controls for the ad container GUI.
 * @param videoElement - The HTML video element used for ad playback.
 * @param imaUnmuter - Utility for handling ad mute/unmute logic.
 * 
 * @example
 * ```typescript
 * class MyImaController extends AbstractImaController {
 *   async buildEnrichedVideoContext(ctx: VideoContext, willAutoplay: boolean): Promise<AdsEnrichedVideoContext> {
 *     // Custom implementation
 *   }
 * }
 * ```
 */
export abstract class AbstractImaController implements AdsController {

    private ima: typeof google.ima;
    private adDisplayContainer: google.ima.AdDisplayContainer;
    private adsLoader: google.ima.AdsLoader;
    private lastVideoElement: HTMLVideoElement;
    protected logger: Logger;


    private static adRequestCounter: number = 0;
    private resizeObserver: ResizeObserver;
    private internalEvents: EventHandler;
    private imaSpecificContainerControls: ImaSpecificAdsContainerControls;
    private events: EventHandler;

    private evtOfInterst: google.ima.AdEvent.Type[];

    protected constructor(ima: typeof google.ima,
        protected window: Window,
        protected gdpr: GDPRSdk,
        protected userAdData: UserAdsData,
        protected prebid: PrebidModule,
        protected imaControllerConfig: ImaControllerConfig,
        protected adResolver: AdCallResolver,
        protected adContainerControls: AdContainerControls,
        protected videoElement: HTMLVideoElement,
        private imaUnmuter: IMAUnmuter
    ) {
        this.ima = ima;
        this.evtOfInterst = [
            google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
            google.ima.AdEvent.Type.USER_CLOSE,
            google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
            google.ima.AdEvent.Type.SKIPPED,
            google.ima.AdEvent.Type.CLICK,
            google.ima.AdEvent.Type.RESUMED,
            google.ima.AdEvent.Type.PAUSED,
            google.ima.AdEvent.Type.LOADED,
            google.ima.AdEvent.Type.STARTED,
            google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
            google.ima.AdEvent.Type.COMPLETE,
            google.ima.AdEvent.Type.VOLUME_CHANGED,
            google.ima.AdEvent.Type.AD_PROGRESS
        ];

        if (!isNumber(this.imaControllerConfig.adImpressionViewableTimeMillis) ||
            this.imaControllerConfig.adImpressionViewableTimeMillis <= 0) {
            this.imaControllerConfig.adImpressionViewableTimeMillis = DEFAULT_AD_VIEWABLE_IMPRESSION_TIME;
        }
        this.events = new SimpleEventHandler();
        this.lastVideoElement = null;
        this.logger = new BrowserLogger("IMA Controller");
        this.internalEvents = new SimpleEventHandler();
        this.resizeObserver = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                const nSize: ContainerResizeEventData = {
                    width: this.adContainerControls.container.clientWidth,
                    height: this.adContainerControls.container.clientHeight,
                    fullscreen: this.adContainerControls.fullscreen
                }
                this.internalEvents.trigger(IMA_CONTROLLER_INTERNAL_EVENTS.CONTAINER_RESIZE, nSize);
            });
        })
        this.resizeObserver.observe(this.adContainerControls.container);
        this.initIMA();

        const shouldBeCCLayerShown = (mng?: google.ima.AdsManager, adInfo?: google.ima.Ad) => {
            const framew = adInfo ? adInfo.getApiFramework() : "vpaid";
            const isVpaid = framew && /vpaid/i.test(framew) ? true : false;
            const isUsed = mng ? mng.isCustomClickTrackingUsed() : false;
            const showLayer = this.imaControllerConfig.features.enableCustomClickTracking && !isVpaid && isUsed;
            this.logger.debug("Showing cclayer?", showLayer, "isVpaid", isVpaid, "isUsed", isUsed, "isEnabled", this.imaControllerConfig.features.enableCustomClickTracking);
            return showLayer;
        }
        const closeButtonCheckAndActivate = (mng?: google.ima.AdsManager, adInfo?: google.ima.Ad) => {
            if (!adInfo.isLinear()) {
                this.adContainerControls.showCloseControl();
                this.adContainerControls.onNextCloseClick(() => {
                    if (mng) closeAdFun(mng);
                });
            }
            else this.adContainerControls.hideCloseControl();
        }

        this.imaSpecificContainerControls = {
            show: (mng?: google.ima.AdsManager, adInfo?: google.ima.Ad) => {
                closeButtonCheckAndActivate(mng, adInfo);
                this.adContainerControls.show(adInfo);
                if (!shouldBeCCLayerShown(mng, adInfo)) this.adContainerControls.hideCustomClickLayer();
                else this.adContainerControls.showCustomClickLayer();

            },
            hide: () => {
                this.adContainerControls.hide();
            },
            refresh: (mng?: google.ima.AdsManager, adInfo?: google.ima.Ad) => {
                this.adContainerControls.refresh();
                if (!shouldBeCCLayerShown(mng, adInfo)) this.adContainerControls.hideCustomClickLayer();
                else this.adContainerControls.showCustomClickLayer();
                closeButtonCheckAndActivate(mng, adInfo);

            },
            signalPlayStatus: (newStatus: boolean) => {
                this.adContainerControls.signalPlayStatus(newStatus)
            },
            signalAudioStatus: (newStatus: boolean) => {
                this.adContainerControls.signalAudioStatus(newStatus)
            },
        }

    }
    onAdEvent(listener: PlayerAdEventListener): PlayerAdEventAction {
        const rListener: Action = (name: string, evt: any) => {
            try {
                if (isPlayerAdEvent(evt)) listener(evt);
            } catch (e) {
                this.logger.error(e);
            }
        }

        this.events.on(AD_EVENT_NAME, rListener);
        return rListener;
    }
    offAdEvent(listener: PlayerAdEventAction): void {
        this.events.off(listener);
    }

    protected triggerAdEvent(evt: PlayerAdEvent): void {
        this.events.trigger(AD_EVENT_NAME, evt);
    }


    /**
     * @returns ad container gui controls
     */
    public getAdContainerControls(): ImaSpecificAdsContainerControls {
        return this.imaSpecificContainerControls;
    }


    /**
     * initializes ima sdk
     */
    private initIMA() {
        if (this.lastVideoElement != this.videoElement) {
            this.lastVideoElement = this.videoElement;
            if (this.adDisplayContainer) {
                this.logger.debug("Destroying old ads display container");
                if (this.adsLoader) this.adsLoader.destroy();
                this.adDisplayContainer.destroy();
                this.adsLoader = null;
                this.adDisplayContainer = null;
            }
            this.logger.debug("Initializing container and loader")
            google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true);
            google.ima.settings.setLocale("it");
            google.ima.settings.setNumRedirects(8);
            if (this.gdpr.google.allowPersonalizedAds()
                && !this.userAdData.isOptOut()) {
                const apat = this.userAdData.getApat();
                if (apat) google.ima.settings.setPpid(apat);
            }
            if (this.imaControllerConfig.features.autoPlayAdBreaks) google.ima.settings.setAutoPlayAdBreaks(true);

            if (this.imaControllerConfig.features.enableCustomClickTracking) {
                this.adDisplayContainer = new google.ima.AdDisplayContainer(this.adContainerControls.container, this.videoElement, this.adContainerControls.customClickLayer);
            } else {
                this.adDisplayContainer = new google.ima.AdDisplayContainer(this.adContainerControls.container, this.videoElement);
            }


            const imaDivInjected = Array.from(this.adContainerControls.container.children).find(e => e.classList.length == 0)

            if (imaDivInjected) {
                (imaDivInjected as HTMLElement).style.width = "100%";
                (imaDivInjected as HTMLElement).style.height = "100%";
            }

            this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);
            if (this.imaControllerConfig.features.vpaid) google.ima.settings.setVpaidMode(this.imaControllerConfig.features.vpaid);
            // this.adDisplayContainer.initialize();

        }
    }

    /**
     * creates a `google.ima.AdRequest` object with proper settings
     * @param tagUrl ad call url
     * @returns 
     */
    private createRequest(tagUrl: string): google.ima.AdsRequest {

        this.adDisplayContainer.initialize();
        const adsRequest = new google.ima.AdsRequest();
        adsRequest.adTagUrl = tagUrl;
        // da verificare i settings che oggi applichiamo al player attuale

        const size = this.getAdAreaSize();

        // const useW = this.videoElement.clientWidth;
        // const useH = this.videoElement.clientHeight;
        this.logger.debug("Setting Ad Request init sizes to:", size.width, "x", size.height);

        adsRequest.linearAdSlotWidth = size.width;
        adsRequest.linearAdSlotHeight = size.height;
        adsRequest.nonLinearAdSlotWidth = size.width;
        adsRequest.nonLinearAdSlotHeight = size.height / 3;

        if (this.imaControllerConfig.videoPolicy == VideoPolicyRules.GOOGLE_UPDATED_VIDEO_POLICY) {
            adsRequest.setAdWillPlayMuted(this.imaUnmuter.declareMute());
        }
        else {
            //?REQ: richiesta di fabrizio, non dichiarare l'autoplay
            adsRequest.setAdWillAutoPlay(true);

        }
        // adsRequest.setContinuousPlayback(true);
        adsRequest.vastLoadTimeout = 8000;
        adsRequest.forceNonLinearFullSlot = true;
        if (this.imaControllerConfig.omid) {
            adsRequest.omidAccessModeRules = this.imaControllerConfig.omid;
        }
        return adsRequest;
    }


    /**
     * creates proper `google.ima.AdsRenderingSettings`
     * @returns 
     */
    private getRenderingSettings(): google.ima.AdsRenderingSettings {
        const settings = new google.ima.AdsRenderingSettings();
        //settings.enablePreloading = this.imaControllerConfig.features.mediaPreloading;
        if (this.imaControllerConfig.features.styledAds) {
            settings.useStyledLinearAds = true;
            settings.useStyledNonLinearAds = true;
        }
        settings.autoAlign = true;
        settings.loadVideoTimeout = 10000;
        settings.uiElements = [google.ima.UiElements.AD_ATTRIBUTION, google.ima.UiElements.COUNTDOWN];

        return settings;
    }



    protected getAdAreaSize(): { width: number, height: number } {
        return {
            width: this.adContainerControls.container.clientWidth,
            height: this.adContainerControls.container.clientHeight
        }
    }





    /**
     * Builds a `WrappedImaAdsManager` starting from an `google.ima.AdsManagerLoadedEvent`
     * @param evt 
     * @returns 
     */
    private buildAdManager(adUnit: AdUnit, resource: VideoRef, evt: google.ima.AdsManagerLoadedEvent): WrappedImaAdsManager {

        const imaMng = evt.getAdsManager(this.videoElement, this.getRenderingSettings());
        imaMng.configureAdsManager(this.videoElement)
        let alreadyInitCl: boolean = false
        let completable = false;
        let currentBreak: AdBreak = null;
        let currentAd: google.ima.Ad = null;
        let viewableTriggered = false;
        let ensureEnabled = false;
        let ensureAfter = 0;
        if (isNumber(this.imaControllerConfig.ensureResourceLoadAfterPrerollPercentage)) {
            ensureEnabled = true;
            ensureAfter = this.imaControllerConfig.ensureResourceLoadAfterPrerollPercentage;
            ensureAfter = ensureAfter > 0 && ensureAfter < 1 ? ensureAfter : 0.5;
        }



        const baseEventData = (etype: PlayerAdEventType) => {
            return {
                executionId: resource.executionId,
                adUnit: adUnit,
                mediaId: resource.info.id,
                eventType: etype,
            };
        }

        const breakEventData = (etype: PlayerAdEventType): PlayerAdBreakRelatedEvent => {
            const useBreak: AdBreak = isAdBreak(adUnit) ? adUnit : currentBreak;
            if (useBreak) {
                return {
                    ...baseEventData(etype),
                    adBreak: useBreak
                }
            }
            else return null;
        };

        const adInfoEvtData = (etype: PlayerAdEventType, ad: google.ima.Ad): PlayerAdEventWithInfo => {
            const adInfo = {
                title: ad.getTitle(),
                linear: ad.isLinear(),
                duration: ad.getDuration(),
                adSystem: ad.getAdSystem(),
                adId: ad.getAdId()
            }
            return {
                ...breakEventData(etype),
                adInfo: adInfo
            }
        }



        const mng: WrappedImaAdsManager = {
            get unit() {
                return adUnit;
            },
            resizeListener: (evt: string, data?: any) => {
                if (evt == IMA_CONTROLLER_INTERNAL_EVENTS.CONTAINER_RESIZE &&
                    data && isContainerResizeEventData(data)) {
                    imaMng.resize(data.width, data.height, data.fullscreen ? google.ima.ViewMode.FULLSCREEN : google.ima.ViewMode.NORMAL)
                }
            },
            get adsManager() {
                this.init();
                return imaMng;
            },
            init: () => {
                if (!alreadyInitCl) {
                    alreadyInitCl = true;
                    const size = this.getAdAreaSize();
                    this.logger.debug("Setting Ad Manager init sizes to:", size.width, "x", size.height);
                    imaMng.init(size.width, size.height, google.ima.ViewMode.NORMAL);
                   
                   // #13804 se l'elemento video è muto, ma la dichiarazione di mute è false allora setto il volume dell'admanager a 0.001 
                    if (resource.element.muted) {
                        if (this.imaUnmuter.declareMute())
                            imaMng.setVolume(0)
                        else
                            imaMng.setVolume(0.001)

                    }
                    //imaMng.setVolume(0);
                }

            },
            close: () => {
                closeAdFun(imaMng);
            },
            start: () => {
                imaMng.start();
                if (isPrebidModuleWithAdsManagerOps(this.prebid)) {
                    return this.prebid.beforeAdManagerStart(getGoogleType(this.window), adUnit, imaMng, resource)
                }
                else return Promise.resolve();
            },
            adsLoader: this.adsLoader,
            evtRetrigger: (evt: google.ima.AdEvent) => {
                let propEvt: PlayerAdEvent = null;
                switch (evt.type) {
                    case google.ima.AdEvent.Type.SKIPPED:
                    case google.ima.AdEvent.Type.USER_CLOSE:
                        propEvt = breakEventData(PlayerAdEventType.AD_USER_CLOSE);
                        currentAd = null;
                        break;
                    case google.ima.AdEvent.Type.ALL_ADS_COMPLETED:
                    case google.ima.AdEvent.Type.COMPLETE:
                        if (completable) {
                            completable = false;
                            propEvt = adInfoEvtData(PlayerAdEventType.AD_COMPLETED, evt.getAd());
                            currentAd = null;
                        }
                        break;
                    case google.ima.AdEvent.Type.CLICK:
                        propEvt = breakEventData(PlayerAdEventType.AD_CLICKED);
                        break;
                    case google.ima.AdEvent.Type.LOADED:
                        let bType: AdBreakType = AdBreakType.PREROLL;
                        currentAd = evt.getAd();
                        const adpos: number = evt.getAd().getAdPodInfo().getAdPosition();
                        switch (evt.getAd().getAdPodInfo().getTimeOffset()) {
                            case -1:
                                bType = AdBreakType.POSTROLL;
                                break;
                            case 0:
                                bType = AdBreakType.PREROLL;
                                break;
                            default:
                                bType = AdBreakType.MIDROLL;
                                break;
                        }
                        currentBreak = {
                            breakType: bType,
                            label: bType + (adpos > 1 ? adpos : "")
                        };


                        propEvt = adInfoEvtData(PlayerAdEventType.AD_LOADED, evt.getAd());
                        this.imaUnmuter.beforeAdStart(mng.adsManager, currentAd)
                        break;
                    case google.ima.AdEvent.Type.STARTED:
                        this.adContainerControls.signalAdWaiting(false);
                        this.adContainerControls.show(evt.getAd())
                        completable = true;
                        viewableTriggered = false;
                        propEvt = adInfoEvtData(PlayerAdEventType.AD_STARTED, evt.getAd())
                        break;
                    case google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED:
                    case google.ima.AdEvent.Type.RESUMED:
                        propEvt = adInfoEvtData(PlayerAdEventType.AD_RESUMED, evt.getAd());
                        break;
                    case google.ima.AdEvent.Type.PAUSED:
                        propEvt = adInfoEvtData(PlayerAdEventType.AD_PAUSED, evt.getAd());
                        break;
                    case google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED:
                        break;
                    case google.ima.AdEvent.Type.AD_PROGRESS:
                        const currentTime: number = evt.getAdData()?.currentTime;
                        const duration: number = evt.getAdData()?.duration;
                        if (completable && !(typeof currentTime == "undefined") && !(typeof duration == "undefined")) {
                            this.adContainerControls.signalAdProgress(currentTime, duration)
                            if (!viewableTriggered && (currentTime * 1000) > this.imaControllerConfig.adImpressionViewableTimeMillis) {
                                viewableTriggered = true;
                                propEvt = breakEventData(PlayerAdEventType.AD_VIEWABILTY);
                            }
                            if (ensureEnabled && (currentTime / duration) >= ensureAfter && isAdBreak(adUnit) && adUnit.breakType == AdBreakType.PREROLL) {
                                this.logger.debug("ENSURING RESOURCE LOAD @", currentTime, "on", duration);
                                resource.ensureResourceLoad();
                            }
                        }
                        break;
                }

                if (propEvt) this.triggerAdEvent(propEvt);
            },
            errRetrigger: (err: google.ima.AdErrorEvent) => {
                this.adContainerControls.signalAdWaiting(false);
                const propEvt = breakEventData(PlayerAdEventType.AD_PLAY_ERROR);
                propEvt.error = true;
                propEvt.errorData = buildErrorStruct(this.window, true,
                    (isAdBreak(adUnit) ? adUnit : currentBreak).label, err.getError(), currentAd, resource.info);
                if (propEvt) this.triggerAdEvent(propEvt);
            }

        }
        if (this.imaControllerConfig.responsive) {
            this.internalEvents.on(IMA_CONTROLLER_INTERNAL_EVENTS.CONTAINER_RESIZE, mng.resizeListener);
        }



        this.evtOfInterst.forEach(e => imaMng.addEventListener(e, mng.evtRetrigger));
        imaMng.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, mng.errRetrigger);
        return mng;
    }


    /**
     * destroys ad manager and related resources
     * @param mng 
     */
    public destroyAdManager(mng: WrappedImaAdsManager): void {
        this.logger.debug("Doing destroyAdManager")

        if (this.imaControllerConfig.responsive) {
            this.internalEvents.off(IMA_CONTROLLER_INTERNAL_EVENTS.CONTAINER_RESIZE, mng.resizeListener);
        }
        this.evtOfInterst.forEach(e => mng.adsManager.removeEventListener(e, mng.evtRetrigger));
        mng.adsManager.removeEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, mng.errRetrigger);
        // mng.adsLoader.contentComplete();
        mng.adsManager.stop();
        mng.adsManager.destroy();
    }




    public newWrappedAdRequest(unit: AdUnit, resource: VideoRef): WrappedImaAdRequest {
        return new WrappedImaAdRequestDefaultImpl(unit, resource, () => {
            const toTrigger: PlayerAdUnitRelatedEvent = {
                eventType: PlayerAdEventType.AD_WILL_REQUEST,
                executionId: resource.executionId,
                adUnit: unit,
                mediaId: resource.info.id
            }
            this.triggerAdEvent(toTrigger)

            return this.prebid.bidAndGetEnricher(unit, resource.info).then(enricher => {
                return this.adResolver(unit, resource.info, enricher({}));
            })
        }, (req: ResolvedAdUnit) => {
            return this.executeRequest(unit, resource, req);
        });

    }



    /**
     * Use this method to perform ad requests and get back a `WrappedImaAdsManager` to be
     * used to perform operations inside `AdsEnrichedVideoContext` implementations
     * @param unit specific ad unit to request
     * @returns 
     */
    protected executeRequest(unit: AdUnit, resource: VideoRef, resolvedUnit: ResolvedAdUnit): Promise<WrappedImaAdsManager> {

        this.adContainerControls.signalAdWaiting(true);
        const adsRequest = this.createRequest(resolvedUnit.parametrizedUrl);
        const reqId = ++AbstractImaController.adRequestCounter;
        return new Promise((resolve, reject) => {
            this.logger.debug("Doing requestId", reqId);
            const onAMLListener: (evt: google.ima.AdsManagerLoadedEvent) => void = (evt) => {
                this.logger.debug("[AD-EV] MANAGER LOADED EVENT", evt);
                const ctx = evt.getUserRequestContext();
                if (ctx && ("reqId" in ctx) && isNumber(ctx.reqId) && ctx.reqId == reqId) {
                    this.adsLoader.removeEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAMLListener);
                    this.adsLoader.removeEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onErrListener);
                    const mng = this.buildAdManager(unit, resource, evt);
                    resolve(mng);
                }
            }
            const onErrListener: (evt: google.ima.AdErrorEvent) => void = (evt) => {
                this.adContainerControls.signalAdWaiting(false);
                const ctx = evt.getUserRequestContext();
                if (ctx && ("reqId" in ctx) && isNumber(ctx.reqId) && ctx.reqId == reqId) {
                    const adError = evt.getError();
                    const pError: PlayerAdUnitRelatedEvent = {
                        error: true,
                        adUnit: unit,
                        executionId: resource.executionId,
                        eventType: PlayerAdEventType.AD_LOAD_ERROR,
                        errorData: buildErrorStruct(this.window, true,
                            unit.label, adError, null, resource.info),
                        mediaId: resource.info.id
                    };
                    this.logger.error("Load error", evt.getError().getMessage(), evt.getError().getInnerError())
                    this.triggerAdEvent(pError);
                    this.adsLoader.removeEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAMLListener);
                    this.adsLoader.removeEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onErrListener);
                    reject(adError);
                }

            }

            this.adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAMLListener);
            this.adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onErrListener);


            this.adsLoader.requestAds(adsRequest, { reqId: reqId });

            const toTrigger: PlayerAdUnitRelatedEvent = {
                eventType: PlayerAdEventType.AD_REQUESTED,
                executionId: resource.executionId,
                adUnit: unit,
                mediaId: resource.info.id
            }
            this.triggerAdEvent(toTrigger)
        });



    }


    public signalResumableMedia(unit: AdUnit, brk: AdBreak, info: VideoInfoRawData, executionId: ExecutionId): void {
        const toTrigger: PlayerAdBreakRelatedEvent = {
            eventType: PlayerAdEventType.AD_RESUMABLE_MEDIA,
            executionId: executionId,
            mediaId: info.id,
            adUnit: unit,
            adBreak: brk
        }
        this.triggerAdEvent(toTrigger)
    }

    public signalPausedMedia(unit: AdUnit, brk: AdBreak, info: VideoInfoRawData, executionId: ExecutionId): void {
        const toTrigger: PlayerAdBreakRelatedEvent = {
            eventType: PlayerAdEventType.AD_PAUSED_MEDIA,
            executionId: executionId,
            mediaId: info.id,
            adUnit: unit,
            adBreak: brk
        }
        this.triggerAdEvent(toTrigger)
    }


    public getIMASdk(): typeof google.ima {
        return this.ima;
    }



    onStart(): Promise<any> {
        return Promise.resolve();
    }

    /**
     * Implementations of this abstract class should take care of implementing
     * this method to return their specific implementations of the `AdsEnrichedVideoContext`
     * @param ctx 
     */
    abstract buildEnrichedVideoContext(ctx: VideoContext, willAutoplay: boolean): Promise<AdsEnrichedVideoContext>;


    getAdAndWrapContext(ctx: VideoContext, willAutoplay: boolean): Promise<AdsEnrichedVideoContext> {
        this.videoElement = ctx.ref.element;
        this.initIMA();
        const ctls = this.buildEnrichedVideoContext(ctx, willAutoplay);
        return ctls;

    }




}


